import React, { useMemo, useEffect } from 'react';
import { useSearchState } from '../../hooks/useSearchState';
import { extractCount } from '../../utils/helpers';
import { useCollapsibleActions } from '../CollapsibleSection';
import posthog from 'posthog-js';

interface BaseFacetProps {
  field: string;
  label: string;
  show?: number;
  otherAtBottom?: boolean;
  sortType?: 'count' | 'alphabetical';
  children?: (props: any) => React.ReactNode;
  transformOptions?: (options: any[]) => any[];
}

export const BaseFacet: React.FC<BaseFacetProps> = ({
  field,
  label,
  show = 30,
  otherAtBottom = false,
  sortType = 'count',
  children,
  transformOptions,
}) => {
  const { currentState, selectedValues, driver } = useSearchState(field);
  const collapsibleActions = useCollapsibleActions();

  const options = useMemo(() => {
    const facetData = currentState?.facets?.[field];
    let facetOptions: any[] = [];

    // Get options from facet data if available
    if (facetData) {
      // React Search UI format: array of facet objects with data array
      if (Array.isArray(facetData)) {
        facetData.forEach((facetItem: any) => {
          if (facetItem && facetItem.data && Array.isArray(facetItem.data)) {
            // Extract from the data array
            facetItem.data.forEach((item: any) => {
              if (item && item.value != null) {
                facetOptions.push({
                  value: item.value.toString(),
                  count: item.count || 0,
                });
              }
            });
          } else if (facetItem && facetItem.value != null) {
            // Direct array of options
            facetOptions.push({
              value: facetItem.value.toString(),
              count: facetItem.count || 0,
            });
          }
        });
      } else if (typeof facetData === 'object') {
        // Object format: { "value1": count1, "value2": count2 }
        facetOptions = Object.entries(facetData)
          .filter(([value, countData]) => value != null)
          .map(([value, countData]) => ({
            value: value.toString(),
            count: extractCount(countData),
          }));
      }
    }

    // Add selected values that might not be in facet data (0 count case)
    const enhancedOptions = [...facetOptions];
    selectedValues.forEach((selectedValue) => {
      // Ensure selectedValue is not null/undefined
      if (selectedValue != null && selectedValue !== undefined) {
        const stringValue = selectedValue.toString();
        const exists = enhancedOptions.some(
          (option) => option.value === stringValue
        );

        if (!exists) {
          enhancedOptions.push({
            value: stringValue,
            count: 0,
          });
        }
      }
    });

    // Apply custom transformation if provided
    let processedOptions = transformOptions
      ? transformOptions(enhancedOptions)
      : enhancedOptions;

    // Sort logic based on sortType and otherAtBottom
    let sortedOptions;
    if (sortType === 'alphabetical') {
      if (otherAtBottom) {
        sortedOptions = processedOptions.sort((a, b) => {
          const aIsOther = a.value.toLowerCase().includes('other');
          const bIsOther = b.value.toLowerCase().includes('other');

          if (aIsOther && !bIsOther) return 1;
          if (!aIsOther && bIsOther) return -1;

          return a.value.localeCompare(b.value);
        });
      } else {
        sortedOptions = processedOptions.sort((a, b) =>
          a.value.localeCompare(b.value)
        );
      }
    } else {
      // Sort by count
      if (otherAtBottom) {
        sortedOptions = processedOptions.sort((a, b) => {
          const aIsOther = a.value.toLowerCase().includes('other');
          const bIsOther = b.value.toLowerCase().includes('other');

          if (aIsOther && !bIsOther) return 1;
          if (!aIsOther && bIsOther) return -1;

          if (b.count !== a.count) {
            return b.count - a.count;
          }
          return a.value.localeCompare(b.value);
        });
      } else {
        // Sort by count descending, then by value
        sortedOptions = processedOptions.sort((a, b) => {
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          return a.value.localeCompare(b.value);
        });
      }
    }

    // Limit to show count if specified
    return show ? sortedOptions.slice(0, show) : sortedOptions;
  }, [
    currentState,
    field,
    selectedValues,
    show,
    otherAtBottom,
    sortType,
    transformOptions,
  ]);

  const handleToggle = (value: string) => {
    // Ensure value is not null/undefined
    if (value == null || value === undefined) return;

    const stringValue = value.toString();
    const isCurrentlySelected = selectedValues.has(stringValue);

    if (isCurrentlySelected) {
      driver.removeFilter(field, stringValue);
    } else {
      driver.addFilter(field, stringValue, 'any');
    }

    // Track facet selection
    posthog.capture('facet_toggled', {
      field: field,
      value: stringValue,
      action: isCurrentlySelected ? 'removed' : 'added',
    });
  };

  const clearAll = () => {
    selectedValues.forEach((value) => {
      if (value != null && value !== undefined) {
        driver.removeFilter(field, value);
      }
    });

    // Track clear action
    posthog.capture('facet_cleared', {
      field: field,
      values_cleared: Array.from(selectedValues).filter((v) => v != null),
    });
  };

  const hasFilters = selectedValues.size > 0;

  const selectAll = () => {
    options.forEach((option) => {
      if (!selectedValues.has(option.value)) {
        driver.addFilter(field, option.value, 'any');
      }
    });
  };

  // Register selectAll/clearAll with CollapsibleSection context if available
  useEffect(() => {
    if (collapsibleActions) {
      collapsibleActions.registerActions({
        selectAll,
        clearAll,
        optionCount: options.length,
      });
    }
  }, [options, selectedValues, collapsibleActions]);

  // Don't render if no options and no selected values
  if (options.length === 0 && !hasFilters) {
    return null;
  }

  if (children) {
    return (
      <>
        {children({
          options,
          selectedValues,
          handleToggle,
          clearAll,
          selectAll,
          hasFilters,
          label,
          driver,
        })}
      </>
    );
  }

  return (
    <div className="custom-facet-container">
      {label && (
        <div className="custom-facet-header">
          <label className="custom-facet-label">{label}</label>
          {hasFilters && (
            <button onClick={clearAll} className="custom-facet-clear">
              Clear
            </button>
          )}
        </div>
      )}

      <div className="custom-facet-options">
        {options.map((option) => (
          <label
            key={option.value}
            className={`custom-facet-option ${selectedValues.has(option.value) ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={selectedValues.has(option.value)}
              onChange={() => handleToggle(option.value)}
              className="mp-checkbox"
            />
            <span className="flex-1">{option.value}</span>
            <span className="custom-facet-count">({option.count})</span>
          </label>
        ))}
      </div>
    </div>
  );
};
